import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { map, of, switchMap } from 'rxjs';
import { Status } from 'sap';

import { AuthService } from '../services/auth.service';
import { UserStatusService } from '../services/user-status.service';

interface StatusRedirects {
  Active?: string;
  Inactive?: string;
  Locked?: string;
}

/**
 * Factory function to create status-based guards
 * @param allowedStatus - Which status is allowed access to this route
 * @param redirects - Where to redirect users based on their status
 */

function createStatusGuard(allowedStatus: Status['account_status'], redirects: StatusRedirects): CanActivateFn {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  return (_route, _state) => {
    const authService = inject(AuthService);
    const userStatusService = inject(UserStatusService);
    const router = inject(Router);

    return authService.getUserId().pipe(
      switchMap((user) => {
        if (!user) {
          // User not authenticated, redirect to login
          router.navigate(['/login']);
          return of(null);
        }

        // Check if we already have the user status in the service
        const currentStatus = userStatusService.currentStatus;
        if (currentStatus?.id === user.uid) {
          return of(currentStatus);
        }

        // Fetch user status if not already loaded
        return userStatusService.getUserStatus$(user.uid);
      }),
      map((status) => {
        if (!status) {
          // No status available, redirect to login
          router.navigate(['/login']);
          return false;
        }

        // If user has the allowed status, grant access
        if (status.account_status === allowedStatus) {
          return true;
        }

        // Otherwise, redirect based on their actual status
        const redirectPath = redirects[status.account_status];
        if (redirectPath) {
          router.navigate([redirectPath]);
        } else {
          // Default fallback
          router.navigate(['/sections/folders']);
        }

        return false;
      })
    );
  };
}

/**
 * Main app guard - allows Active users, redirects others
 */
export const statusGuard = createStatusGuard('Active', {
  Inactive: '/resubscribe',
  Locked: '/locked',
});

/**
 * Locked page guard - allows Locked users, redirects others
 */
export const lockedStatusGuard = createStatusGuard('Locked', {
  Active: '/sections/folders',
  Inactive: '/resubscribe',
});

/**
 * Resubscribe page guard - allows Inactive users, redirects others
 */
export const inactiveStatusGuard = createStatusGuard('Inactive', {
  Active: '/sections/folders',
  Locked: '/locked',
});

/**
 * Alternative guard that only checks if status is already loaded
 * Use this for routes where you know the status has already been fetched
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const statusCheckGuard: CanActivateFn = (_route, _state) => {
  const userStatusService = inject(UserStatusService);
  const router = inject(Router);

  const currentStatus = userStatusService.currentStatus;

  if (!currentStatus) {
    // Status not loaded, redirect to a loading page or back to main
    router.navigate(['/']);
    return false;
  }

  switch (currentStatus.account_status) {
    case 'Active':
      return true;

    case 'Inactive':
      router.navigate(['/resubscribe']);
      return false;

    case 'Locked':
      router.navigate(['/locked']);
      return false;

    default:
      return true;
  }
};
